/**
 * LanguageSelector Component
 *
 * Language and culture picker for story generation.
 * Fetches available languages from the API and displays them in a styled card.
 */

import { useLanguagesList } from '@/shared/api/generated/languages/languages'
import { Icon } from '@/shared/components/Icon/Icon'
import { pxToRem } from '@/shared/styles/themes'
import { faGlobe } from '@fortawesome/pro-light-svg-icons/faGlobe'
import { Box, FormControl, MenuItem, Select, styled, Typography } from '@mui/material'

interface LanguageSelectorProps {
  selectedLanguageCode: string
  onSelect: (languageCode: string) => void
  disabled?: boolean
}

export const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  selectedLanguageCode,
  onSelect,
  disabled = false,
}) => {
  const { data: languagesData } = useLanguagesList()
  const languages = languagesData?.results ?? []

  if (languages.length <= 1) {
    return null
  }

  return (
    <StyledCard>
      <StyledTitle>
        <Icon icon={faGlobe} />
        <Typography variant="subtitle1" fontWeight={600}>
          Pick a language and culture
        </Typography>
      </StyledTitle>
      <FormControl size="small" fullWidth>
        <Select
          value={selectedLanguageCode}
          onChange={(e) => onSelect(e.target.value as string)}
          disabled={disabled}
          sx={{ borderRadius: pxToRem(8) }}
        >
          {languages.map((lang) => (
            <MenuItem key={lang.code} value={lang.code}>
              <StyledMenuItem>
                <Typography variant="body2">{lang.name_english}</Typography>
                {lang.name_english !== lang.name_native && (
                  <Typography variant="caption" color="text.secondary">
                    {lang.name_native}
                  </Typography>
                )}
              </StyledMenuItem>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </StyledCard>
  )
}

const StyledCard = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: pxToRem(12),
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: pxToRem(16),
  padding: pxToRem(20),
  marginBottom: pxToRem(16),
  maxWidth: pxToRem(400),
  marginLeft: 'auto',
  marginRight: 'auto',
}))

const StyledTitle = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${pxToRem(8)};
`

const StyledMenuItem = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${pxToRem(8)};
`
